import React from "react";
import { Edit, Trash2, Milk, Tablets, Pill, ChevronRight } from "lucide-react";

const COUNT_TYPE_ICON = {
  "MMS Botol": { Icon: Milk, color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  "MMS Blister": { Icon: Tablets, color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  "TTD Capsule": { Icon: Pill, color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
};

const dateKey = (ts) => new Date(ts).toDateString();

const formatDateHeader = (ts) => {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Hari ini";
  if (d.toDateString() === yesterday.toDateString()) return "Kemarin";
  return d.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

const HistoryTable = ({
  records,
  loading,
  isAdmin,
  handleViewRecord,
  handleEditRecord,
  handleDeleteRecord,
  getCountType,
  totalRecords = 0,
}) => {
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-300">Loading records...</p>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="p-8 text-center">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No records found</h3>
        <p className="text-gray-600 dark:text-gray-300">
          {totalRecords === 0 ? "No pill count records yet." : "No records match your search criteria."}
        </p>
      </div>
    );
  }

  const sorted = [...records].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  const groups = [];
  sorted.forEach((record) => {
    const key = dateKey(record.timestamp);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(record);
    else groups.push({ key, label: formatDateHeader(record.timestamp), items: [record] });
  });

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section key={group.key}>
          <h2 className="px-1 mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {group.label}
          </h2>
          <ul className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
            {group.items.map((record) => {
              const countType = getCountType(record);
              const { Icon, color } = COUNT_TYPE_ICON[countType] || COUNT_TYPE_ICON["MMS Botol"];
              return (
                <li key={record.id} className="flex items-center">
                  <button
                    onClick={() => handleViewRecord(record)}
                    className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <span className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${color}`} title={countType}>
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">
                        {record.mother_name && record.mother_name !== "N/A" ? record.mother_name : "-"}
                      </span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                        {countType} · {record.barcode_id || "-"} ·{" "}
                        {new Date(record.timestamp).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm font-semibold text-gray-900 dark:text-white">
                      {record.pill_count} pills
                    </span>
                    {!isAdmin && <ChevronRight className="shrink-0 h-4 w-4 text-gray-400" aria-hidden="true" />}
                  </button>
                  {isAdmin && (
                    <div className="flex items-center pr-3">
                      <button
                        onClick={() => handleEditRecord(record)}
                        className="p-2 text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400"
                        title="Edit Record"
                        aria-label="Edit Record"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRecord(record)}
                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        title="Delete Record"
                        aria-label="Delete Record"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default HistoryTable;
